/**
 * Merchant Pro V1 PR-2 — Analytics surface.
 *
 * Thin route wrapper around the existing <BrandAnalyticsPanel> so the
 * historical KPIs + charts that previously lived inside OverviewPage
 * remain reachable at a dedicated /owner/analytics URL.
 *
 * REUSE discipline (per PR-2 Review Package §1 Sidebar Module #2 +
 * Top Reuse Opportunity #1):
 *   • Reuses <BrandAnalyticsPanel> verbatim — NOT a rebuild.
 *   • Reuses <PageHeader> for the page chrome.
 *   • Reuses <FullSpinner> + <QueryError> for loading / error states.
 *   • Reuses /api/owner/overview (existing endpoint; no new API call).
 *
 * Net new code: ~50 lines of glue. Zero new charts, zero new KPIs,
 * zero duplicate KPI tiles.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuthStore } from '@/lib/auth';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';

import {
  PageHeader,
  FullSpinner,
  QueryError,
} from '@/components/ui_primitives/Misc';
import BrandAnalyticsPanel from '@/components/BrandAnalyticsPanel';
import ReceiptClaimsAnalyticsSection from '@/components/owner/ReceiptClaimsAnalyticsSection';

export default function AnalyticsPage() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const brandId = user?.brand_id;
  const [range, setRange] = useState('7d');

  const overviewQ = useQuery({
    queryKey: qk.ownerOverview(brandId || 'self', range),
    queryFn: () => api.ownerOverview(token, range),
    enabled: !!token,
    staleTime: 30_000,
  });

  return (
    <div className="space-y-6" data-testid="owner-analytics-page">
      <PageHeader
        kicker="Analytics"
        title="Brand Analytics"
        subtitle="Revenue, transactions, customer growth, and top performers across your selected window."
      />
      {overviewQ.isLoading ? (
        <FullSpinner />
      ) : overviewQ.isError ? (
        <QueryError error={overviewQ.error} onRetry={overviewQ.refetch} />
      ) : (
        <BrandAnalyticsPanel
          data={overviewQ.data}
          range={range}
          onRangeChange={setRange}
          onRefresh={() => overviewQ.refetch()}
          isLoading={overviewQ.isLoading}
          isFetching={overviewQ.isFetching}
        />
      )}
      {/* R10.6 (2026-07-05) — Receipt Claims Analytics section rendered
          below the existing panel. Reads the SAME range state so a single
          preset drives both surfaces. Does NOT merge receipt totals into
          POS revenue (operator forbids). */}
      <ReceiptClaimsAnalyticsSection range={range} />
    </div>
  );
}
